import { Controller, useFormContext } from "react-hook-form";
import Button from "../common/button";
import { useAppSelector } from "../../hooks/store-hooks";
import { useEffect, useRef, useState } from "react";
import ProgressBar from "../common/progress-bar";
import LottieAnimation from "../common/animations";
import { useDebounce } from "../../hooks/debounce-hooks";
import { useGetCompanySearchDataQuery } from "../../store/api/searchApi";
import Dropdown from "../common/select/Dropdown";
import Input from "../common/input/input";
import { CaretRight } from "../../icons";

const DEBOUNCE_DELAY = 300;

interface OnboardingData {
  page_data: {
    dropdown_values: string[];
  }[];
}

const ProfessionalDetailsComp = () => {
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();

  const { data: onboardingData } = useAppSelector(
    (state) => state.onboarding || {},
  ) as { data: OnboardingData | undefined };

  const [dropdown_values, setDropdownValues] = useState<
    { value: string; label: string }[]
  >([]);
  const [incomeRanges, setIncomeRanges] = useState<
    { value: string; label: string }[]
  >([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const [companySearchTerm, setCompanySearchTerm] = useState("");

  const debouncedSearchTerm = useDebounce(companySearchTerm, DEBOUNCE_DELAY);

  const { data: companySearchResults, isLoading } =
    useGetCompanySearchDataQuery(
      { q: debouncedSearchTerm || "a" },
      { skip: false },
    );

  const companyDropdownValues =
    companySearchResults?.Company?.map((company: { name: string }) => ({
      value: company.name,
      label: company.name,
    })) || [];

  const [isFocused, setIsFocused] = useState(false);

  const handleCompanyInputChange = (value: string) => {
    setCompanySearchTerm(value || "a");
  };

  useEffect(() => {
    setDropdownValues([]);
    const dropdownValuesFromData = Array.isArray(
      onboardingData?.page_data[0]?.dropdown_values,
    )
      ? onboardingData?.page_data[0].dropdown_values
      : [];
    const newDropdownValues =
      dropdownValuesFromData?.map((value: string) => ({
        value: value,
        label: value,
      })) || [];
    setDropdownValues([...newDropdownValues]);

    const incomeRangeValuesFromData = Array.isArray(
      onboardingData?.page_data[4]?.dropdown_values,
    )
      ? onboardingData?.page_data[4].dropdown_values
      : [];
    const newIncomeRanges =
      incomeRangeValuesFromData?.map((value: string) => ({
        value: value,
        label: value,
      })) || [];
    setIncomeRanges([...newIncomeRanges]);
  }, [onboardingData]);

  useEffect(() => {
    if (inputRef.current) {
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [companySearchTerm]);

  return (
    <div className="h-full flex flex-col justify-between">
      <div>
        <div className="flex items-center flow-root ">
          <div className="w-32 md:mb-10 mb-6 float-left">
            <div
              className="cursor-pointer mb-3 "
              onClick={() => {
                window.history.back();
              }}
            >
              <div className="rotate-180 transform origin-left ml-5">
                <CaretRight />
              </div>
            </div>
            <LottieAnimation lottieUrl="/animation/Logo.lottie" height="40px" />
          </div>

          <div className="mt-12 ml-4 w-1/5 float-right">
            <ProgressBar currentStep={3} totalSteps={5} />
          </div>
        </div>
        <div className="text-2.8xl text-white leading-10 font-bold mb-2">
          Enter Professional Details
        </div>
        <div className="text-2.8xl text-gray-200 leading-1 text-md mb-4">
          Help us know you better for a more personalized app experience
        </div>
        <div className="">
          <div className="w-full mb-5">
            <Controller
              name="industry"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  dropdown_data={dropdown_values}
                  id="industry"
                  name="industry"
                  customClassName="bg-[#262626] text-white"
                  maxLength={30}
                  showError={true}
                  errorMessage={errors?.industry?.message}
                  value={value}
                  onChange={onChange}
                  placeholder="Industry"
                />
              )}
            />
          </div>
          <div className="w-full mb-5">
            <Controller
              name="company_name"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="relative">
                  <Input
                    id="company_name"
                    name="company_name"
                    customClassName="bg-[#262626] text-white px-3"
                    maxLength={30}
                    showError={true}
                    errorMessage={errors?.company_name?.message?.toString()}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                      handleCompanyInputChange(value);
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                    placeholder="Company Name"
                  />
                  {isFocused && (
                    <div
                      className="absolute w-full mt-1 z-10 max-h-[200px] overflow-y-auto"
                      style={{
                        backgroundColor: "#262626",
                        borderRadius: "0.75rem",
                        backgroundImage: "linear-gradient(#262626, black)",
                      }}
                    >
                      {isLoading ? (
                        <div className="p-2 text-gray-300">Loading...</div>
                      ) : (
                        <>
                          {companyDropdownValues.map((option: any) => (
                            <div
                              key={option.value}
                              className="p-2 cursor-pointer text-white hover:bg-[#3e3e3e] border-b border-[#484747] last:border-b-0"
                              onClick={() => {
                                onChange(option.value);
                                setCompanySearchTerm(option.value);
                                setIsFocused(false);
                              }}
                            >
                              {option.label}
                            </div>
                          ))}
                          {companySearchTerm &&
                            companyDropdownValues.length === 0 && (
                              <div
                                className="p-2 cursor-pointer text-white hover:bg-[#3e3e3e] border-b border-[#484747] last:border-b-0"
                                onClick={() => {
                                  onChange(companySearchTerm);
                                  setCompanySearchTerm(companySearchTerm);
                                  setIsFocused(false);
                                }}
                              >
                                Add "{companySearchTerm}"
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          <div className="w-full mb-5">
            <Controller
              name="designation"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input
                  id="designation"
                  name="designation"
                  customClassName="bg-[#262626] text-white px-3"
                  maxLength={30}
                  showError={true}
                  errorMessage={errors?.designation?.message?.toString()}
                  value={value}
                  onChange={onChange}
                  placeholder="Designation"
                />
              )}
            />
          </div>

          <div className="w-full mb-5">
            <Controller
              name="income_range"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  dropdown_data={incomeRanges}
                  id="income_range"
                  name="income_range"
                  customClassName="bg-[#262626] text-white max-h-20"
                  maxLength={30}
                  showError={true}
                  errorMessage={errors?.income_range?.message}
                  value={value}
                  onChange={onChange}
                  placeholder="Annually Income Range"
                />
              )}
            />
          </div>
        </div>
      </div>
      <div>
        <Button
          isSubmitting={isSubmitting}
          customClasses="w-full mb-5 rounded-xl"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default ProfessionalDetailsComp;
